import { useEffect, useRef, useState } from "react";

/**
 * useReveal — slow fade-in on viewport entry.
 * Strategy: assume visible by default to avoid stuck-hidden states, then
 * briefly reset to hidden on mount so the transition runs once.
 */
export default function useReveal({ threshold = 0.12 } = {}) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        // Reduced motion: show immediately, no animation.
        const reduced =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduced) {
            setVisible(true);
            return;
        }

        if (typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }

        // If element is already (mostly) in view at mount, reveal next frame.
        const rect = node.getBoundingClientRect();
        const vh = window.innerHeight || 800;
        if (rect.top < vh * 0.9) {
            const id = requestAnimationFrame(() => setVisible(true));
            return () => cancelAnimationFrame(id);
        }

        const obs = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        obs.unobserve(entry.target);
                    }
                }
            },
            { threshold, rootMargin: "0px 0px -10% 0px" }
        );
        obs.observe(node);

        // Safety net: ensure content shows even if observer fails to fire.
        const fallback = setTimeout(() => setVisible(true), 1200);

        return () => {
            obs.disconnect();
            clearTimeout(fallback);
        };
    }, [threshold]);

    return { ref, visible };
}
